import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MailOpen } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { TablePaginationBar } from '@/components/app/TablePaginationBar'
import { cn } from '@/lib/utils'
import { fetchNotifications } from '../../notifications/api'

function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d)
}

function severityToneClass(severity: string) {
  const s = severity.toLowerCase()
  if (s === 'error' || s === 'critical') {
    return 'border-transparent bg-rose-500/10 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
  }
  if (s === 'warning') {
    return 'border-transparent bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
  }
  if (s === 'success' || s === 'info') {
    return 'border-transparent bg-sky-500/10 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300'
  }
  return 'border-transparent bg-muted text-muted-foreground'
}

export function NotificationHistoryTable() {
  const { t, i18n } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    select: (res) => res.data,
  })

  const rows = useMemo(() => {
    if (!notificationsQuery.data) return []
    return [...notificationsQuery.data].sort(
      (a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime(),
    )
  }, [notificationsQuery.data])

  const totalItems = rows.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const pageSafe = Math.min(page, totalPages)

  const pageSlice = useMemo(() => {
    const start = (pageSafe - 1) * pageSize
    return rows.slice(start, start + pageSize)
  }, [rows, pageSafe, pageSize])

  if (notificationsQuery.isPending) {
    return (
      <div className="overflow-hidden rounded-lg border border-border">
        <div className="space-y-0">
          <Skeleton className="h-10 w-full rounded-none" />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-none border-t border-border/50" />
          ))}
        </div>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center gap-3.5 bg-card px-4 py-5">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted/40 text-muted-foreground">
          <MailOpen className="size-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {t('settings.notifications.noHistory')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('settings.notifications.noHistoryDescription')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.notifications.table.title')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.notifications.table.message')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.notifications.table.severity')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.notifications.table.status')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-right text-xs font-medium text-muted-foreground">
                {t('settings.notifications.table.receivedAt')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageSlice.map((notification) => {
              const isRead = notification.read_at !== null

              return (
                <TableRow key={notification.id} className={cn('group', !isRead && 'bg-sky-500/[0.03]')}>
                  <TableCell className="py-2.5">
                    <div className="flex items-center gap-2">
                      {!isRead && (
                        <span className="inline-block size-1.5 rounded-full bg-sky-500" />
                      )}
                      <span className={cn('text-sm', !isRead && 'font-medium text-foreground')}>
                        {notification.title}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 text-sm text-muted-foreground">
                    <div className="max-w-[18rem] truncate" title={notification.message}>
                      {notification.message}
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5">
                    <Badge
                      variant="outline"
                      className={cn(
                        'rounded-full text-[11px] font-medium capitalize',
                        severityToneClass(notification.severity),
                      )}
                    >
                      {t(`common.${notification.severity}`, { defaultValue: notification.severity })}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2.5">
                    <span className={cn('text-sm', isRead ? 'text-muted-foreground' : 'text-foreground font-medium')}>
                      {isRead ? t('settings.notifications.status.read') : t('settings.notifications.status.unread')}
                    </span>
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-sm tabular-nums text-muted-foreground">
                    {formatDateTime(notification.created_at, i18n.language)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      <TablePaginationBar
        page={pageSafe}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        pageSizeOptions={[5, 10, 25, 50]}
      />
    </div>
  )
}
